import type {
  ClaimStatus,
  IsOfferClaimed,
  OfferEntrySource,
  OfferScreen,
  OfferType,
  WalletSection,
} from "../../offers/constants";

/** Emit-side names for offer raw events. */
export enum OfferRawEventName {
  ViewedOfferBanner = "ViewedOfferBanner",
  TappedOfferBanner = "TappedOfferBanner",
  ViewedOfferTile = "ViewedOfferTile",
  TappedOfferTile = "TappedOfferTile",
  ViewedOfferDetailsModal = "ViewedOfferDetailsModal",
  TappedOfferModalCta = "TappedOfferModalCta",
  OffersEntry = "OffersEntry",
  TappedOfferSearch = "TappedOfferSearch",
  HotelTappedListCard = "HotelTappedListCard",
  TappedSeeAllHotels = "TappedSeeAllHotels",
  WalletViewed = "WalletViewed",
  CreditHistoryModalViewed = "CreditHistoryModalViewed",
  ViewedAllOffers = "ViewedAllOffers",
}

// Property shapes must be `type` aliases, not `interface` — the tracking `AEvent`
// constraint needs `Record<string, unknown>`, which interfaces don't satisfy.

export type ViewedOfferBannerProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  screen: OfferScreen;
  is_offer_eligible: boolean;
  is_offer_claimed: IsOfferClaimed;
  is_claimable: boolean;
};

export type TappedOfferBannerProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  screen: OfferScreen;
  claim_status: ClaimStatus;
  claim_failure_reason?: string;
  is_offer_claimed: IsOfferClaimed;
  is_claimable: boolean;
};

/** Base tile properties shared by viewed and tapped tile events. */
export type OfferTileBaseProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  destination: string;
  destination_label: string;
  destination_country_code: string;
  tile_index: number;
  discount_percentage: number;
  screen: OfferScreen;
  is_claimable: boolean;
  wallet_section?: WalletSection;
};

/**
 * Keep for backward compat with `offerTileTracking.ts` which builds the base
 * properties before the component adds claim/screen/wallet fields.
 */
export type OfferTileProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  destination: string;
  destination_label: string;
  destination_country_code: string;
  tile_index: number;
  discount_percentage: number;
};

export type ViewedOfferTileProperties = OfferTileBaseProperties & {
  is_offer_claimed: IsOfferClaimed;
};

export type TappedOfferTileProperties = OfferTileBaseProperties & {
  claim_status: ClaimStatus;
  claim_failure_reason?: string;
};

export type ViewedOfferDetailsModalProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  screen: OfferScreen;
  points_multiplier?: number;
  offer_valid_until?: string;
  is_offer_claimed: IsOfferClaimed;
  is_claimable: boolean;
  wallet_section?: WalletSection;
};

export type TappedOfferModalCtaProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  screen: OfferScreen;
  is_claimable: boolean;
};

export type OffersEntryProperties = {
  offer_type: OfferType;
  offer_id: string;
  offer_name: string;
  destination: string;
  destination_label: string;
  destination_country_code: string;
  entry_source: OfferEntrySource;
  is_claimable: boolean;
};

export type TappedOfferSearchProperties = {
  offer_id: string;
  offer_name: string;
  offer_type: OfferType;
  destination: string;
  check_in_date?: string;
  check_out_date?: string;
  los?: number;
  advance?: number;
  adults_count: number;
  children_count: number;
  is_claimable: boolean;
};

export type HotelTappedListCardProperties = {
  screen: OfferScreen;
  offer_type?: OfferType;
  offer_id?: string;
  offer_name?: string;
  is_claimable?: boolean;
};

export type TappedSeeAllHotelsProperties = {
  offer_id: string;
  offer_name: string;
  offer_type: OfferType;
  destination: string;
  is_claimable: boolean;
};

export type WalletViewedProperties = {
  credits_balance_amount: number;
  credits_balance_currency: string;
  offers_count: number;
  offers_list: string[];
  avion_points_balance?: number;
  claimed_offers_count: number;
  entry_source: string;
};

export type CreditHistoryModalViewedProperties = {
  credits_balance_amount: number;
  credits_balance_currency: string;
};

export type ViewedAllOffersProperties = {
  entry_source: string;
  offers_count: number;
  claimed_offers_count: number;
  offers_list: string[];
};

// --- Raw events -------------------------------------------------------------

export interface ViewedOfferBannerRawEvent {
  name: OfferRawEventName.ViewedOfferBanner;
  properties: ViewedOfferBannerProperties;
}

export interface TappedOfferBannerRawEvent {
  name: OfferRawEventName.TappedOfferBanner;
  properties: TappedOfferBannerProperties;
}

export interface ViewedOfferTileRawEvent {
  name: OfferRawEventName.ViewedOfferTile;
  properties: ViewedOfferTileProperties;
}

export interface TappedOfferTileRawEvent {
  name: OfferRawEventName.TappedOfferTile;
  properties: TappedOfferTileProperties;
}

export interface ViewedOfferDetailsModalRawEvent {
  name: OfferRawEventName.ViewedOfferDetailsModal;
  properties: ViewedOfferDetailsModalProperties;
}

export interface TappedOfferModalCtaRawEvent {
  name: OfferRawEventName.TappedOfferModalCta;
  properties: TappedOfferModalCtaProperties;
}

export interface OffersEntryRawEvent {
  name: OfferRawEventName.OffersEntry;
  properties: OffersEntryProperties;
}

export interface TappedOfferSearchRawEvent {
  name: OfferRawEventName.TappedOfferSearch;
  properties: TappedOfferSearchProperties;
}

export interface HotelTappedListCardRawEvent {
  name: OfferRawEventName.HotelTappedListCard;
  properties: HotelTappedListCardProperties;
}

export interface TappedSeeAllHotelsRawEvent {
  name: OfferRawEventName.TappedSeeAllHotels;
  properties: TappedSeeAllHotelsProperties;
}

export interface WalletViewedRawEvent {
  name: OfferRawEventName.WalletViewed;
  properties: WalletViewedProperties;
}

export interface CreditHistoryModalViewedRawEvent {
  name: OfferRawEventName.CreditHistoryModalViewed;
  properties: CreditHistoryModalViewedProperties;
}

export interface ViewedAllOffersRawEvent {
  name: OfferRawEventName.ViewedAllOffers;
  properties: ViewedAllOffersProperties;
}

export type OfferRawEvent =
  | ViewedOfferBannerRawEvent
  | TappedOfferBannerRawEvent
  | ViewedOfferTileRawEvent
  | TappedOfferTileRawEvent
  | ViewedOfferDetailsModalRawEvent
  | TappedOfferModalCtaRawEvent
  | OffersEntryRawEvent
  | TappedOfferSearchRawEvent
  | HotelTappedListCardRawEvent
  | TappedSeeAllHotelsRawEvent
  | WalletViewedRawEvent
  | CreditHistoryModalViewedRawEvent
  | ViewedAllOffersRawEvent;
